import { ComponentType } from 'react';
import { Organization } from '../types';

// Import farm components
import { AnimalPassport } from './farm/components/AnimalPassport';
import { SalesList } from './farm/components/SalesList';
import { AnimalList } from './farm/components/AnimalList';
import { AnimalListView } from './farm/components/AnimalListView';
import { NutritionView } from './farm/components/NutritionView';
import AnimalNutritionHistoryView from './farm/components/AnimalNutritionHistoryView';
import LotNutritionHistoryView from './farm/components/LotNutritionHistoryView';
import { WeaningFeedbackView } from './farm/components/WeaningFeedbackView';

// Import slaughterhouse components
import { CarcassReport } from './slaughterhouse/components/CarcassReport';

// Import dairy components
import { MilkQualityReport } from './dairy/components/MilkQualityReport';

// Import cooperative components
import { MembershipReport } from './cooperative/components/MembershipReport';

// Import common components
import { PriceReport } from './common/components/PriceReport';

// Define the structure for view registry
interface ViewConfig {
  component: ComponentType<any>;
  name: string;
  description: string;
}

interface OrganizationViews {
  [viewKey: string]: ViewConfig;
}

interface ViewRegistry {
  [organizationType: string]: OrganizationViews;
}

// Central registry mapping organization types to their available views
export const viewRegistry: ViewRegistry = {
  farm: {
    animalPassport: {
      component: AnimalPassport,
      name: 'Passaporte do Animal',
      description: 'Informações detalhadas sobre um animal específico'
    },
    animalList: {
      component: AnimalList,
      name: 'Lista de Animais',
      description: 'Visualização de todos os animais do rebanho'
    },
    'animal-list': {
      component: AnimalListView,
      name: 'Lista de Animais da Fazenda',
      description: 'Lista detalhada dos animais encontrados na consulta'
    },
    salesList: {
      component: SalesList,
      name: 'Animais à Venda',
      description: 'Lista de animais disponíveis para venda'
    },
    priceReport: {
      component: PriceReport,
      name: 'Relatório de Preços',
      description: 'Tendências e preços atuais do mercado'
    },
    nutrition: {
      component: NutritionView,
      name: 'Manejo Nutricional',
      description: 'Registro de fornecimento de nutrientes e ração'
    },
    animalNutritionHistory: {
      component: AnimalNutritionHistoryView,
      name: 'Histórico Nutricional do Animal',
      description: 'Histórico completo de alimentação de um animal específico'
    },
    lotNutritionHistory: {
      component: LotNutritionHistoryView,
      name: 'Histórico Nutricional do Lote',
      description: 'Histórico completo de alimentação de um lote específico'
    },
    weaningFeedback: {
      component: WeaningFeedbackView,
      name: 'Feedback de Desmama',
      description: 'Tela de confirmação e detalhes da operação de desmama'
    }
  },
  slaughterhouse: {
    carcassReport: {
      component: CarcassReport,
      name: 'Relatório de Carcaça',
      description: 'Análise de rendimento e qualidade de lotes processados'
    },
    priceReport: {
      component: PriceReport,
      name: 'Relatório de Preços',
      description: 'Preços de compra e tendências de mercado'
    }
  },
  dairy: {
    milkQualityReport: {
      component: MilkQualityReport,
      name: 'Qualidade do Leite',
      description: 'Análises laboratoriais e controle de qualidade'
    },
    priceReport: {
      component: PriceReport,
      name: 'Relatório de Preços',
      description: 'Preços do leite e derivados'
    }
  },
  cooperative: {
    membershipReport: {
      component: MembershipReport,
      name: 'Relatório de Associados',
      description: 'Gestão de membros e contribuições'
    },
    priceReport: {
      component: PriceReport,
      name: 'Relatório de Preços',
      description: 'Preços consolidados para cooperados'
    }
  }
};

// Helper function to get available views for an organization type
export const getAvailableViews = (organizationType: Organization['type']): OrganizationViews => {
  return viewRegistry[organizationType] || {};
};

// Helper function to get a specific view component
export const getViewComponent = (
  organizationType: Organization['type'], 
  viewKey: string
): ComponentType<any> | null => {
  const views = getAvailableViews(organizationType);
  return views[viewKey]?.component || null;
};

// Helper function to check if a view exists for an organization
export const hasView = (
  organizationType: Organization['type'], 
  viewKey: string
): boolean => {
  const views = getAvailableViews(organizationType);
  return !!views[viewKey];
};

// Get view metadata
export const getViewMetadata = (
  organizationType: Organization['type'], 
  viewKey: string
): { name: string; description: string } | null => {
  const views = getAvailableViews(organizationType);
  const view = views[viewKey];
  return view ? { name: view.name, description: view.description } : null;
};